<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container-custom py-8">
      <h1 class="text-3xl font-bold text-gray-900 mb-8">
        {{ $t('cart.title') }}
      </h1>
      
      <div v-if="cartStore.isEmpty" class="text-center py-16">
        <ShoppingCartIcon class="h-20 w-20 text-gray-300 mx-auto mb-4" />
        <h2 class="text-xl font-semibold text-gray-900 mb-2">
          {{ $t('cart.empty') }}
        </h2>
        <p class="text-gray-500 mb-6">{{ $t('cart.emptyMessage') }}</p>
        <router-link
          to="/menu"
          class="inline-flex items-center px-6 py-3 rounded-lg text-white font-medium"
          :style="{ backgroundColor: companyStore.primaryColor }"
        >
          {{ $t('cart.browseMenu') }}
        </router-link>
      </div>
      
      <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Cart Items -->
        <div class="lg:col-span-2 space-y-4">
          <div
            v-for="(item, index) in cartStore.items"
            :key="index"
            class="card p-4 flex gap-4"
          >
            <!-- Image -->
            <img
              v-if="item.product.image_url"
              :src="getImageUrl(item.product.image_url)"
              :alt="item.product.name"
              class="w-24 h-24 object-cover rounded-lg"
            >
            <div v-else class="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center">
              <PhotoIcon class="h-8 w-8 text-gray-300" />
            </div>
            
            <!-- Details -->
            <div class="flex-1">
              <h3 class="font-semibold text-gray-900">{{ item.product.name }}</h3>
              <p class="text-sm text-gray-500 mb-2">
                {{ formatCurrency(item.product.current_price) }} cada
              </p>
              
              <!-- Options -->
              <div v-if="item.selected_options.length" class="text-sm text-gray-600 mb-2">
                <span v-for="opt in item.selected_options" :key="opt.name" class="mr-2">
                  {{ opt.name }}: {{ opt.value }}
                </span>
              </div>
              
              <!-- Addons -->
              <div v-if="item.selected_addons.length" class="text-sm text-gray-600 mb-2">
                <span v-for="addon in item.selected_addons" :key="addon.name" class="mr-2">
                  + {{ addon.name }}
                </span>
              </div>
              
              <!-- Quantity -->
              <div class="flex items-center gap-4">
                <div class="flex items-center border rounded-lg">
                  <button
                    @click="updateQuantity(index, item.quantity - 1)"
                    class="p-2 hover:bg-gray-100"
                  >
                    <MinusIcon class="h-4 w-4" />
                  </button>
                  <span class="px-4 font-medium">{{ item.quantity }}</span>
                  <button
                    @click="updateQuantity(index, item.quantity + 1)"
                    class="p-2 hover:bg-gray-100"
                  >
                    <PlusIcon class="h-4 w-4" />
                  </button>
                </div>
                
                <button
                  @click="removeItem(index)"
                  class="text-red-500 hover:text-red-700"
                >
                  <TrashIcon class="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <!-- Total -->
            <div class="text-right">
              <p class="font-semibold text-gray-900">{{ formatCurrency(item.total) }}</p>
            </div>
          </div>
          
          <button
            @click="cartStore.clearCart()"
            class="text-red-500 hover:text-red-700 text-sm font-medium"
          >
            {{ $t('cart.clearCart') }}
          </button>
        </div>
        
        <!-- Summary -->
        <div class="lg:col-span-1">
          <div class="card p-6 sticky top-24">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">
              {{ $t('checkout.review.orderSummary') }}
            </h2>
            
            <div class="space-y-3 mb-6">
              <div class="flex justify-between text-gray-600">
                <span>{{ $t('cart.subtotal') }}</span>
                <span>{{ cartStore.formattedSubtotal }}</span>
              </div>
              <div class="flex justify-between text-gray-600">
                <span>{{ $t('cart.deliveryFee') }}</span>
                <span>{{ cartStore.deliveryFee > 0 ? cartStore.formattedDeliveryFee : $t('cart.freeDelivery') }}</span>
              </div>
              <div v-if="cartStore.discount > 0" class="flex justify-between text-green-600">
                <span>{{ $t('cart.discount') }}</span>
                <span>-{{ cartStore.formattedDiscount }}</span>
              </div>
              <div class="border-t pt-3 flex justify-between text-lg font-semibold">
                <span>{{ $t('cart.total') }}</span>
                <span :style="{ color: companyStore.primaryColor }">{{ cartStore.formattedTotal }}</span>
              </div>
            </div>
            
            <router-link
              to="/checkout"
              class="block w-full text-center px-6 py-3 rounded-lg text-white font-medium"
              :style="{ backgroundColor: companyStore.primaryColor }"
            >
              {{ $t('cart.checkout') }}
            </router-link>
            
            <router-link
              to="/menu"
              class="block w-full text-center mt-3 text-gray-600 hover:text-gray-900"
            >
              {{ $t('cart.continueShopping') }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ShoppingCartIcon,
  PhotoIcon,
  MinusIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/vue/24/outline'
import { useCartStore, useCompanyStore } from '@/stores'
import { formatCurrency } from '@/utils/formatters'

const cartStore = useCartStore()
const companyStore = useCompanyStore()

const getImageUrl = (url: string | undefined) => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return url
}

const updateQuantity = (index: number, quantity: number) => {
  cartStore.updateQuantity(index, quantity)
}

const removeItem = (index: number) => {
  cartStore.removeItem(index)
}
</script>
